import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { ageGroupNaming,
         Capabilities,
         capabilitiesNaming,
         employmentStatusNaming,
         facilityStatusNaming,
         LeadershipRole,
         leadershipRoleNaming,
         positionAchievedNaming,
         qualificationsNaming,
         RegisteredPosition,
         registeredPositionNaming,
         Turf,
         turfNaming, } from '../api/typesDc';
import { DayLabels, labelPicker } from '../api/types';
// import { t } from 'i18next';

//fetch leaguename using year and clubId
const getLeagueNameByYearAndClub = (year:any, clubId:any, leagueData:any) => {
  const yearData = leagueData.find((data:any) => data.year == year);
  if (!yearData) {
    return `-`;
  }

  const league = yearData.leagues.find((league:any) => league.clubs.includes(clubId));
  if (!league) {
    return `-`;
  }

  return league.leagueName;
};

//fetch accounting labels
export const fetchLabel = (type:any, name:any, t:any) => {
  const tempStr = type + name;
  switch (tempStr) {
    case t('TOTAL') + t('DC_3_INCOME'):
      return t('DC_3_CLUB_TOTAL');
    case t('TOTAL') + t('DC_3_EXPENSE'):
      return t('CLUB_EXPENSE');
    case t('ACADEMY_TOTAL') + t('DC_3_INCOME'):
      return t('ACADEMY_TOTAL');
    case t('ACADEMY_TOTAL') + t('DC_3_EXPENSE'):
      return t('ACADEMY_EXPENSE');
  }
};

const trainingLeagueId = 2011;
let fileName = 'ALL_CLUBS_EXPORT';
//Company Details
export type companyDetailsProps = {
    companyName: string;
    academyDirector: string;
    executiveMember: string;
    name: string;
    department: string;
    phone: string;
    fax: string;
    mobile: string;
    workLocation: string;
    email: string;
    year: string;
};
export const companyDetailsHeader = [
    'CLUB_NAME',
    'DC_1_COMPANY_TRADING_NAME',
    'DC_1_NAME_OF_EXEC',
    'DC_1_ACADEMY_DIRECTOR',
    'NAME',
    'DC_2_DEPARTMENT_POSITION',
    'DC_2_MOBILE_PHONE_NUMBER',
    'DC_2_FAX',
    'DC_2_TELEPHONE_NUMBER',
    'DC_2_EMAIL_ADDRESS',
    'DC_2_WORK_LOCATION',
    'LEAGUE',
    'YEAR'
]
export const restructureCompanyDetails = (data:any, isBulkExport:any, leagueHistory:any) => {
    let restructuredData = data.map((d:any, i: number) => {
        const club = d.clubName;
        if(!isBulkExport) {
            fileName = club;
        }
        return [club, d.companyName, d.executiveMember, d.academyDirector, d.name, d.department, d.phone, d.fax, d.mobile, d.email, d.workLocation, getLeagueNameByYearAndClub(d.year, d.clubId, leagueHistory), d.year];
    })
    return restructuredData;
}

//Accounting Items
export type accountingItemsProps = {
    id: number;
    type: 'Income' | 'Expense' | 'Total';
    mandatory: boolean;
    subType: string;
    name: string;
    year: string;
    currentYear: number;
    nextYear: number;
    otherExpenseName: string;
};
export const accountingItemsHeader = [
    'CLUB_NAME',
    'DC_3_LINE_ITEM',
    'DC_3_SUB_LINE_ITEM',
    'ACTUAL(¥)',
    'DC_3_BUDGET',
    'LEAGUE',
    'YEAR'
]
export const restructureAccountingItems = (data:any, isBulkExport:any, t:any, leagueHistory:any) => {
  let restructuredData: any = [];
  data.forEach((d: any, i: number) => {
    if ((isBulkExport && d.club.leagueId != trainingLeagueId) || !isBulkExport) {
      const club = d?.club?.name;
      restructuredData.push([
        club,
        t(labelPicker(d.name)),
        d?.type == t('ACADEMY_TOTAL') || d?.type == t('TOTAL')
          ? fetchLabel(d?.type, d?.name, t)
          : t(labelPicker(d.otherExpenseName)),
        d.currentYear,
        d.nextYear,
        getLeagueNameByYearAndClub(d.year, d.clubId, leagueHistory),
        d.year,
      ]);
    }
  });
  return restructuredData;
};

//Support Fund
export type supportFundProps = { id: number; description: string; amount: number; };
export const supportFundHeader = [
    'CLUB_NAME',
    'DESCRIPTION',
    'DC_4_AMOUNT',
    'DC_4_SUPPORT_FUND_CONTRIBUTION',
    'LEAGUE',
    'YEAR'
]
export const restructureSupportFund = (data:any, isBulkExport:any, leagueHistory:any) => {
    let restructuredData:any = [];
    data.forEach((d:any, i:number) => {
        if((isBulkExport && d.club.league.id != trainingLeagueId) || !isBulkExport) {
            const club = d?.club?.name;
            restructuredData.push([club, d.description, d.amount, d.supportFundContribution ? '1' : '0', getLeagueNameByYearAndClub(d.year, d.clubId, leagueHistory), d.year]);
        }
    })
    return restructuredData;
}

//Staffs
export const staffsHeader = [
    'CLUB_NAME',
    'DC_5_KENJI_NAME',
    'DC_5_KATAKANA_NAME',
    'DC_5_ENGLISH_NAME',
    'DC_5_DOB',
    'DC_5_JFA_ID',
    'DC_5_IRN',
    'DC_5_EMPLOYMENT_STATUS',
    'DEPARTMENT',
    'DC_5_LICENCE_START_DATE',
    'DC_5_LICENCE_EXPIRY_DATE',
    'DC_5_REGISTERED_POSITION',
    'DC_5_JOB_TITLE',
    'DC_5_LEADERSHIP_ROLE',
    'DC_5_QUALIFICATIONS',
    'DC_5_ANNUAL_SALARY',
    'LEAGUE',
    'YEAR'
]
export const restructureStaffs = (data:any, isBulkExport:any, t:any, leagueHistory:any) => {
    let staffs = data.staffs;
    let departments = data.departments;
    let restructuredData:any = [];
    staffs.forEach((d:any, i:number) => {
        if((isBulkExport && d.club.league.id != trainingLeagueId) || !isBulkExport) {
            let department = d.department.map((el:any) => departments.find((d:any) => d.id == el)?.name).join(', ');
            let translatedRP = d.registeredPosition == undefined ? [] : d.registeredPosition.map((rp:RegisteredPosition) => t(registeredPositionNaming[rp]));
            let registeredPositions = translatedRP.length == 0 ? '' : translatedRP.join(',');
            let translatedLR = d.leadershipRole == undefined ? [] : d.leadershipRole.map((lr:LeadershipRole) => t(leadershipRoleNaming[lr]));
            let leadershipRole = translatedLR.length == 0 ? '' : translatedLR.join(', ');
            let translatedQualifications = d.qualifications == undefined ? [] : d.qualifications.map((lr:any) => t(qualificationsNaming[lr]));
            let qualifications = translatedQualifications.length == 0 ? '' : translatedQualifications.join(', ');
            const club = d?.club?.name;
            restructuredData.push([club, d.kanjiName, d.katakanaName, d.englishName,
                d.dateOfBirth == undefined ? '' : format(new Date(d.dateOfBirth), 'yyyy.MM.dd'),
                d.jfaId, d.instructorRegistrationNumber,                        
                d.employmentStatus == undefined ? '' : t(employmentStatusNaming[d.employmentStatus]), department, 
                d.licenceStart == undefined ? '' :format(new Date(d.licenceStart), 'yyyy.MM.dd'), 
                d.licenceEnd == undefined ? '' :format(new Date(d.licenceEnd), 'yyyy.MM.dd'), 
                registeredPositions, d.jobTitle, leadershipRole, qualifications, d.annualSalary, getLeagueNameByYearAndClub(d.year, d.clubId, leagueHistory), d.year]);
        }
    })
    return restructuredData;
}

//Staffs Experience
export const staffsExperienceHeader = [
    'CLUB_NAME',
    'DC_5_KENJI_NAME',
    'DC_5_ENGLISH_NAME',
    'CAREER_TYPE',
    'CLUB',
    'YEAR_FROM',
    'YEAR_TO',
    'LEAGUE',
    'YEAR'
]
export const restructureStaffsExperience = (data:any, isBulkExport:any, t:any, leagueHistory:any) => {
    let i = 0;
    let careers: any = [];
    
    data.forEach((d: any) => {
        if ((isBulkExport && d.club.league.id != trainingLeagueId) || !isBulkExport) {
            const club = d?.club?.name;
            const { coachingCareers = [], competitionCareers = [], kanjiName, englishName, year } = d;
            const coachingCareer = t('DC_5_COACHING_CAREER');
            const competitionCareer = t('DC_5_COMPETITION_CAREER');
    
            coachingCareers.forEach((c: any) => {
                i = i + 1;
                careers.push([club, kanjiName, englishName, coachingCareer, c.club, c.yearFrom, c.yearTo, getLeagueNameByYearAndClub(year, d.clubId, leagueHistory), year]);
            });
    
            competitionCareers.forEach((c: any) => {
                i = i + 1;
                careers.push([club, kanjiName, englishName, competitionCareer, c.club, c.yearFrom, c.yearTo, getLeagueNameByYearAndClub(year, d.clubId, leagueHistory), year]);
            });
        }
    });
    
    return careers;    
}

//Facilities
export const facilitiesHeader = [
    'CLUB_NAME',
    'DC_6_FACILITY_NAME',
    'DC_6_SHORT_NAME',
    'DC_6_ADDRESS',
    'DC_6_OWNER',
    'DC_6_FACILITY_STATUS',
    'DC_6_CAPABILITIES',
    'DC_6_N_OF_CHANGING_ROOMS',
    'DC_6_DORMITORY_CAPACITY',
    'DC_6_DORMITORY_REMARKS',
    'LEAGUE',
    'YEAR'
]
export const restructureFacilities = (data:any, isBulkExport:any, t:any, leagueHistory:any) => {
    let restructuredData:any = [];
    data.forEach((d:any, i:number) => {
        if((isBulkExport && d?.club?.league?.id != trainingLeagueId) || !isBulkExport) {
            const club = d?.club?.name;
            let translatedCapablities = (d.capabilities == undefined || typeof d.capabilities == 'string') ? [] : d.capabilities.map((lr:Capabilities) => t(capabilitiesNaming[lr]));
            let capabilities = translatedCapablities.lenght == 0 ? '' : translatedCapablities.join(', ');
            restructuredData.push([club, d.facilityName, d.shortName, d.address, d.owner, t(facilityStatusNaming[d.facilityStatus]), capabilities, d.changingRooms, d.dormitoryCapacity, d.dormitoryRemarks, getLeagueNameByYearAndClub(d.year, d.clubId, leagueHistory), d.year]);
        }
    })
    return restructuredData;
}

//Facility Grounds
export const facilityGroundHeader = [
    'CLUB_NAME',
    'DC_6_FACILITY_NAME',
    'DC_6_SPECTATORS',
    'DC_6_TURF',
    'ILLUMINATION',
    'FACILITY_ID',
    'DC_6_WIDTH',
    'DC_6_DEPTH',
    'LEAGUE',
    'YEAR'
]
export const restructureFacilityGround = (data:any, isBulkExport:any, t:any, leagueHistory:any) => {
    let i = 0;
    let grounds:any = [];
    data.map((d:any) => {
        if((isBulkExport && d?.club?.league?.id != trainingLeagueId) || !isBulkExport) {
            const club = d?.club?.name;
            d.facilityGrounds.map((f:any) => {
                let translatedTurf = (f.turf == undefined || typeof f.turf == 'string') ? [] : f.turf.map((lr:Turf) => t(turfNaming[lr]));
                let turf = translatedTurf.length == 0 ? '' : translatedTurf.join(', ');
                let illumination = (f.illumination == undefined || typeof f.illumination == 'string') ? '' : f.illumination.join(', ');
                i = i+1;
                grounds.push([club, f.name, f.spectators, turf, illumination, f.id, f.width, f.depth, getLeagueNameByYearAndClub(d.year, d.clubId, leagueHistory), d.year]);
            })
        }
    } 
    )
    return grounds;
}

//Team Details
export const teamDetailsHeader = [
    'CLUB_NAME',
    'TEAM_NAME',
    'DC_7_ESTABLISHED',
    'DC_7_AGE_GROUP',
    'DC_7_STAFF_COACHES',
    'DC_7_STAFF_GK_COACHES',
    'DC_7_MEDICAL_STAFF',
    'DC_7_PRACTICE_GROUNDS',
    'DC_7_STAFF_SUPERVISOR',
    'DC_7_ADMISSION_FEES',
    'DC_7_PLAYER_MO_EXPENSE',
    'DC_7_TRAVER_MO_EXPENSE',
    'DC_7_REMARKS',
    'DC_AG_U9',
    'DC_AG_U10',
    'DC_AG_U11',
    'DC_AG_U12',
    'DC_AG_U13',
    'DC_AG_U14',
    'DC_AG_U15',
    'DC_AG_U16',
    'DC_AG_U17',
    'DC_AG_U18',
    'LEAGUE',
    'YEAR'
]
export const restructureTeamDetails = (data:any, isBulkExport:any, t:any, leagueHistory:any) => {
    const teams = data.teams;
    const staffs = data.staffs;
    const facilities:any = [];
    
    data.facilities.map((fac:any) => facilities.push(...fac.facilityGrounds));
    let restructuredData:any = [];
    teams.forEach((d:any, i:number) => {
        if((isBulkExport && d.club.league.id != trainingLeagueId) || !isBulkExport) {
            const club = d?.club?.name;
            let teamDetails = d.teamDetails;
            //staff coaches
            let staffCoaches:any = [];
            teamDetails.staffCoaches.map((coachId:any) => {
                if(coachId !== -1) {
                    let staff = staffs.find((s:any) => s.id == coachId);
                    staff && Object.keys(staff).includes('kanjiName') &&
                    staffCoaches.push(staff.kanjiName);
                }
            });
            staffCoaches = staffCoaches.join(', ');
            //staff GK Coaches
            let staffGKCoaches:any = [];
            teamDetails.staffGkCoaches.map((coachId:any) => {
                if(coachId !== -1) {
                    let staff = staffs.find((s:any) => s.id == coachId);
                    staff && Object.keys(staff).includes('kanjiName') &&
                    staffGKCoaches.push(staff.kanjiName);
                }
            });
            staffGKCoaches = staffGKCoaches.join(', ');
            //medical staff
            let medicalStaff:any = [];
            teamDetails.medicalStaff.map((coachId:any) => {
                if(coachId !== -1) {
                    let staff = staffs.find((s:any) => s.id == coachId);
                    staff && Object.keys(staff).includes('kanjiName') &&
                    medicalStaff.push(staff.kanjiName);
                }
            });
            medicalStaff = medicalStaff.join(', ');
            //practice grounds
            let practiceGrounds:any = [];
            teamDetails.practiceGrounds.map((groundId:any) => {
                if(groundId !== -1) {
                    let ground = facilities.find((s:any) => s.id == groundId);
                    ground && Object.keys(ground).includes('name') &&
                    practiceGrounds.push(ground.name);
                }
            });
            practiceGrounds = practiceGrounds.join(', ');
            restructuredData.push([club, teamDetails.teamName, teamDetails.established, t(ageGroupNaming[teamDetails.ageGroup]), staffCoaches, staffGKCoaches, medicalStaff,
            practiceGrounds, d.staffSupervisor, d.teamFees.admissionFees, d.teamFees.playerMonthlyExpense, d.teamFees.travelMonthlyExpense, d.teamFees.remarks, 
            d.playerAges.u9, d.playerAges.u10, d.playerAges.u11, d.playerAges.u12, d.playerAges.u13, d.playerAges.u14,
            d.playerAges.u15, d.playerAges.u16, d.playerAges.u17, d.playerAges.u18, getLeagueNameByYearAndClub(d.year, d.clubId, leagueHistory), d.year
        ]);
        }
    })
    return restructuredData;
}

//Team Competition
export const teamCompetitionHeader = [
    'CLUB_NAME',
    'TEAM_NAME',
    'DC_7_COMPETITION',
    'DC_7_POSITION_ACHIEVED',
    'LEAGUE',
    'YEAR'
]
export const restructureTeamCompetition = (data:any, isBulkExport:any, t:any, leagueHistory:any) => {
    let i = 0;
    let competition:any = [];
    data.map((d:any) => {
        if((isBulkExport && d.club.league.id != trainingLeagueId) || !isBulkExport) {
            const club = d?.club?.name;
            d.competitions.map((f:any) => {
                i = i+1;
                competition.push([club, d.teamDetails.teamName, f.competition, t(positionAchievedNaming[f.position]), getLeagueNameByYearAndClub(f.year, d.clubId, leagueHistory), f.year]);
            })
        }
    } 
    )
    return competition;
}

//Team Titles
export const teamTitlesHeader = [
    'CLUB_NAME',
    'TEAM_NAME',
    'DC_7_COMPETITION',
    'DC_7_POSITION_ACHIEVED',
    'DC_7_YEAR_ACHIEVED',
    'LEAGUE',
    'YEAR'
]
export const restructureTeamTitles = (data:any, isBulkExport:any, t:any, leagueHistory:any) => {
    let i = 0;
    let competition:any = [];
    data.map((d:any) => {
        if((isBulkExport && d.club.league.id != trainingLeagueId) || !isBulkExport) {
            const club = d?.club?.name;
            d.titles.map((f:any) => {
                if(f.competition !== 'None') {
                    i = i+1;
                    competition.push([club, d.teamDetails.teamName, f.competition, t(positionAchievedNaming['PA_WINNER']), f.yearAchieved, getLeagueNameByYearAndClub(f.year, d.clubId, leagueHistory), f.year]);
                }
            })
        }
    } 
    )
    return competition;
}

//Team Schedule
export const teamScheduleHeader = [
    'CLUB_NAME',
    'TEAM_NAME',
    'DC_7_DAY',
    'DC_7_TRAINIG_START',
    'DC_7_TRAINIG_END',
    'DC_7_GROUNDS',
    'DC_7_REMARKS',
    'LEAGUE',
    'YEAR'
]
export const restructureTeamSchedule = (data:any, isBulkExport:any, t:any, leagueHistory:any) => {
    let i = 0;
    const teams = data.teams;
    const facilities:any = [];
    data.facilities.map((fac:any) => facilities.push(...fac.facilityGrounds));
    let schedule:any = [];
    teams.map((d:any) => {
        if((isBulkExport && d.club.league.id != trainingLeagueId) || !isBulkExport) {
            let f = d.trainingSchedule;
            const club = d?.club?.name;
            Object.keys(d.trainingSchedule).forEach((key, index) => {
                if(key !== 'id' && key !== 'year') {
                    i = i+1;
                    //practice grounds
                    let practiceGrounds:any = [];
                    (f[key].grounds === undefined || f[key].grounds.length === 0) ? practiceGrounds.push('_') : 
                    f[key].grounds.map((groundId: any) => {
                        if(groundId !== -1) {
                            let ground = facilities.find((s: any) => s.id === groundId);
                            ground && Object.keys(ground).includes('name') &&
                            practiceGrounds.push(ground.name);
                        }
                        return groundId;
                    });
                    practiceGrounds = practiceGrounds.join(', ');
                    schedule.push([club, d.teamDetails.teamName, t(DayLabels[key]), f[key].start == undefined ? '-' : format(new Date(f[key].start), 'HH:mm:SS'), 
                    f[key].end == undefined ? '-' : format(new Date(f[key].end), 'HH:mm:SS'), practiceGrounds, f[key].remarks, getLeagueNameByYearAndClub(f.year, d.clubId, leagueHistory), f.year]);
                } else {
                    index--;
                }
            })
        }
    }
    )
    return schedule;
}

//Team Players Count
// export const teamStrengthHeader = [
//     'CLUB_NAME',
//     'TEAM_NAME',
//     'DC_AG_U9',
//     'DC_AG_U10',
//     'DC_AG_U11',
//     'DC_AG_U12',
//     'DC_AG_U13',
//     'DC_AG_U14',
//     'DC_AG_U15',
//     'DC_AG_U16',
//     'DC_AG_U17',
//     'DC_AG_U18',
//     'YEAR'
// ]
// export const restructureTeamStrength = (data:any, isBulkExport) => {
//     const resturucturedData:any = [];
//     data.forEach((d,i) => {
//         if((isBulkExport && d?.club?.league?.id != trainingLeagueId) || !isBulkExport) {
//             const club = d?.club?.name;
//             resturucturedData.push([club, d.teamDetails.teamName, d.playerAges.u9, d.playerAges.u10, d.playerAges.u11, d.playerAges.u12, d.playerAges.u13, d.playerAges.u14,
//                 d.playerAges.u15, d.playerAges.u16, d.playerAges.u17, d.playerAges.u18, d.year]);
//         }
//     })
//     return resturucturedData;
// }

//Players Details Career
export const playerDetailsHeader = [
    'CLUB_NAME',
    'NAME',
    'DC_8_DATE_OF_BIRTH',
    'PLAYER_ID',
    'LEAGUE',
    'YEAR'
]
export const restructurePlayerDetails = (data:any, isBulkExport:any, leagueHistory:any) => {
    // const [t] = useTranslation();
    let restructuredData:any = [];
    data.forEach((d:any, i:number) => {
        if((isBulkExport && d.club.league.id != trainingLeagueId) || !isBulkExport) {
            const club = d?.club?.name;
            
            restructuredData.push([club, d.name, 
                d.dateOfBirth == undefined ? '' : format(new Date(d.dateOfBirth), 'yyyy.MM.dd'),
                d.playerId, getLeagueNameByYearAndClub(d.year, d.clubId, leagueHistory), d.year]);
        }
    })
    return restructuredData;
}

//Players Academy Details
export const playerAcademyDetailsHeader = [
    'CLUB_NAME',
    'NAME',
    'START_DATE',
    'END_DATE',
    'LEAGUE',
    'YEAR'
]
export const restructurePlayerAcademyDetails = (data:any, isBulkExport:any, leagueHistory:any) => {
    // const [t] = useTranslation();
    let restructuredData:any = [];
    data.forEach((d:any, i:number) => {
        if((isBulkExport && d.club.league.id != trainingLeagueId) || !isBulkExport) {
            const club = d?.club?.name;
            let dates = d.academyPeriods;
            
            dates.forEach((x:any) => {
                restructuredData.push([club, d.name,
                    (x.dateFrom == null || x.dateFrom == undefined) ? 'NA' : format(new Date(x.dateFrom), 'yyyy.MM.dd'),
                    (x.dateTo == null || x.dateTo == undefined) ? 'NA' : format(new Date(x.dateTo), 'yyyy.MM.dd'), getLeagueNameByYearAndClub(x.year, d.clubId, leagueHistory), x.year]);
            })
        }
    })
    return restructuredData;
}

//Players Career
export const playersCareerHeader = [
    'CLUB_NAME',
    'NAME',
    'CAREER_TYPE',
    'DC_8_AGE_GROUP',
    'DC_8_COUNTRY',
    'CLUB',
    'START_DATE',
    'END_DATE',
    'LEAGUE',
    'YEAR'
]
export const restructurePlayersCareer = (data:any, isBulkExport:any, t:any, leagueHistory:any) => {
    let i = 0;
    let careers: any = [];
    
    // Club careers
    data.forEach((d: any) => {
        if ((isBulkExport && d.club.league.id != trainingLeagueId) || !isBulkExport) {
            const club = d?.club?.name;
            d.clubCareers.forEach((f: any) => {
                i = i + 1;
                careers.push([club, d.name, t('DC_8_CLUB_CAREER'), '-', f.country, f.club,
                    f.dateFrom == undefined ? '' : format(new Date(f.dateFrom), 'yyyy.MM.dd'),
                    f.dateTo == undefined ? '' : format(new Date(f.dateTo), 'yyyy.MM.dd'), getLeagueNameByYearAndClub(f.year, d.clubId, leagueHistory), f.year]);
            });
        }
    });
    
    //International careers
    data.forEach((d: any) => {
        if ((isBulkExport && d.club.league.id != trainingLeagueId) || !isBulkExport) {
            const club = d?.club?.name;
            d.internationalCareers.forEach((f: any) => {
                i = i + 1;
                let ageGroup = t(f.ageGroup);
                careers.push([club, d.name, t('DC_8_INTERNATIONAL_CAREER'), ageGroup, f.country, f.club,
                    f.dateFrom == undefined ? '' : format(new Date(f.dateFrom), 'yyyy.MM.dd'),
                    f.dateTo == undefined ? '' : format(new Date(f.dateTo), 'yyyy.MM.dd'), getLeagueNameByYearAndClub(f.year, d.clubId, leagueHistory), f.year]);
            });
        }
    });

    return careers;
}

//generating a xlsx file here
export const generateXLSXFile = (props:any) => {
    const { data, isBulkExport, t } = props;

// Create a new workbook
const workbook = XLSX.utils.book_new();

// Adding the Company Details Sheet
const tCompanyDetailsHeader = companyDetailsHeader.map((d) => t(d));
const sheet1:any = XLSX.utils.aoa_to_sheet([
    tCompanyDetailsHeader,
    ...restructureCompanyDetails(data.companyDetails, isBulkExport, data.leagueHistory)
]);

XLSX.utils.book_append_sheet(workbook, sheet1, t('DC_1_COMPONENT_NAME'));


// Adding the Acounting Items Sheet
const tAccountingItemsHeader = accountingItemsHeader.map((d) => t(d));
const sheet2 = XLSX.utils.aoa_to_sheet([
    tAccountingItemsHeader,
    ...restructureAccountingItems(data.accountingItems, isBulkExport, t, data.leagueHistory)
  ]);
XLSX.utils.book_append_sheet(workbook, sheet2, t('ACCOUNTING_ITEMS'));

// Adding the Support Fund Sheet
const tSupportFundHeader = supportFundHeader.map((d) => t(d));
const sheet3 = XLSX.utils.aoa_to_sheet([
    tSupportFundHeader,
  ...restructureSupportFund(data.supportFund, isBulkExport, data.leagueHistory)
]);
XLSX.utils.book_append_sheet(workbook, sheet3, t('DC_4_COMPONENT'));

// Adding the Staffs Sheet
const tStaffsHeader = staffsHeader.map((d) => t(d));
const sheet4 = XLSX.utils.aoa_to_sheet([
    tStaffsHeader,
    ...restructureStaffs(data, isBulkExport, t, data.leagueHistory)
  ]);
XLSX.utils.book_append_sheet(workbook, sheet4, t('DC_5_STAFF'));

// Adding the Staffs Experience
const tStaffsExperienceHeader = staffsExperienceHeader.map((d) => t(d));
const sheet5 = XLSX.utils.aoa_to_sheet([
    tStaffsExperienceHeader,
    ...restructureStaffsExperience(data.staffs, isBulkExport, t, data.leagueHistory)
  ]);
XLSX.utils.book_append_sheet(workbook, sheet5, t('STAFF_CAREER'));

// Adding the Facility Sheet
const tFacilitiesHeader = facilitiesHeader.map((d) => t(d));
const sheet6 = XLSX.utils.aoa_to_sheet([
    tFacilitiesHeader,
    ...restructureFacilities(data.facilities, isBulkExport, t, data.leagueHistory)
  ]);
XLSX.utils.book_append_sheet(workbook, sheet6, t('DC_3_FACILITIES'));

// Adding the Facility Grounds Sheet
const tFacilityGroundHeader = facilityGroundHeader.map((d) => t(d));
const sheet7 = XLSX.utils.aoa_to_sheet([
    tFacilityGroundHeader,
    ...restructureFacilityGround(data.facilities, isBulkExport, t, data.leagueHistory)
  ]);
XLSX.utils.book_append_sheet(workbook, sheet7, t('DC_6_GROUNDS'));

// Adding the Team Details Sheet
const tTeamDetailsHeader = teamDetailsHeader.map((d) => t(d));
const sheet8 = XLSX.utils.aoa_to_sheet([
    tTeamDetailsHeader,
    ...restructureTeamDetails(data, isBulkExport, t, data.leagueHistory)
]);
XLSX.utils.book_append_sheet(workbook, sheet8, t('DC_7_TEAM_DETAILS') + ' & ' + t('DC_7_TEAM_FEES'));


// Adding the Team Competition Sheet
const tTeamCompetitionHeader = teamCompetitionHeader.map((d) => t(d));
const sheet9 = XLSX.utils.aoa_to_sheet([
    tTeamCompetitionHeader,
    ...restructureTeamCompetition(data.teams, isBulkExport, t, data.leagueHistory)
]);
XLSX.utils.book_append_sheet(workbook, sheet9, t('DC_7_COMPETITION'));

// Adding the Team Titles Sheet
const tTeamTitlesHeader = teamTitlesHeader.map((d) => t(d));
const sheet10 = XLSX.utils.aoa_to_sheet([
    tTeamTitlesHeader,
    ...restructureTeamTitles(data.teams, isBulkExport, t, data.leagueHistory)
]);
XLSX.utils.book_append_sheet(workbook, sheet10, t('DC_7_TITLES'));

// Adding the Team Schedule Sheet
const tTeamScheduleHeader = teamScheduleHeader.map((d) => t(d));
const sheet11 = XLSX.utils.aoa_to_sheet([
    tTeamScheduleHeader,
    ...restructureTeamSchedule(data, isBulkExport, t, data.leagueHistory)
]);
XLSX.utils.book_append_sheet(workbook, sheet11, t('DC_7_TRAINING_SCHEDULE'));

// Adding the Team Players Count Sheet
// const tTeamStrengthHeader = teamStrengthHeader.map((d) => t(d));
// const sheet12 = XLSX.utils.aoa_to_sheet([
//     tTeamStrengthHeader,
//     ...restructureTeamStrength(data.teams, isBulkExport)
// ]);
// XLSX.utils.book_append_sheet(workbook, sheet12, t('TEAM_PLAYERS'));

// Adding the Player Details Sheet
const tPlayerDetailsHeader = playerDetailsHeader.map((d) => t(d));
const sheet13 = XLSX.utils.aoa_to_sheet([
    tPlayerDetailsHeader,
    ...restructurePlayerDetails(data.playerDetails, isBulkExport, data.leagueHistory)
]);
XLSX.utils.book_append_sheet(workbook, sheet13, t('DC_8_COMPONENT_NAME'));

// Adding the Player Academy Details Sheet
const tPlayerAcademyDetailsHeader = playerAcademyDetailsHeader.map((d) => t(d));
const sheet15 = XLSX.utils.aoa_to_sheet([
    tPlayerAcademyDetailsHeader,
    ...restructurePlayerAcademyDetails(data.playerDetails, isBulkExport, data.leagueHistory)
]);
XLSX.utils.book_append_sheet(workbook, sheet15, `${t('DC_8_COMPONENT_NAME')} ${t('DC_8_ACADEMY_DETAILS')}`);

// Adding the Players Career Sheet
const tPlayersCareerHeader = playersCareerHeader.map((d) => t(d));
const sheet14 = XLSX.utils.aoa_to_sheet([
    tPlayersCareerHeader,
    ...restructurePlayersCareer(data.playerDetails, isBulkExport, t, data.leagueHistory)
]);
XLSX.utils.book_append_sheet(workbook, sheet14, t('PLAYER_CAREER'));

// Save the workbook to a file
fileName = 'LICENCE_DATA'
const date = new Date();
const year = date.getFullYear();
const month = String(date.getMonth() + 1).padStart(2, '0');
const day = String(date.getDate()).padStart(2, '0');
const dateLabel = `${year}${month}${day}`;
XLSX.writeFile(workbook, `${t(fileName)}_${dateLabel}.xlsx`);
console.log('Excel file created successfully.');

}